import React from 'react';
import styled from 'styled-components';
import { FiFacebook, FiTwitter, FiInstagram, FiLinkedin, FiMail, FiMapPin, FiPhone } from 'react-icons/fi';

const FooterContainer = styled.footer`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #1E40AF 100%);
  color: white;
  padding: 60px 0 30px;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  margin-bottom: 40px;
`;

const FooterSection = styled.div`
  h3 {
    font-size: 1.3rem;
    margin-bottom: 20px;
    color: var(--warm-gold);
  }

  p, li {
    line-height: 1.6;
    margin-bottom: 8px;
    opacity: 0.9;
  }

  ul {
    list-style: none;
    padding: 0;
  }

  a {
    color: white;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: var(--warm-gold);
    }
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const SewaLogoImage = styled.img`
  height: 50px;
  width: auto;
  margin-bottom: 8px;
  /* Use original logo colors */
`;

const SewaLogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const SewaLogoFallback = styled.div`
  background: linear-gradient(135deg, #4C1D95 0%, #6366F1 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 700;
  text-align: center;
  line-height: 1.1;
  min-width: 60px;
  margin-bottom: 8px;
`;

const BayAreaText = styled.div`
  font-size: 0.6rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--warm-gold);
  margin-top: 4px;
`;

const LogoText = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
`;

const BurritoIcon = styled.div`
  width: 28px;
  height: 28px;
  background: linear-gradient(45deg, #D4A574, #F4D03F);
  border-radius: 6px 3px 3px 6px;
  position: relative;
  border: 2px solid #B8860B;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    position: absolute;
    width: 16px;
    height: 2px;
    background: #228B22;
    top: 6px;
    border-radius: 1px;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 14px;
    height: 2px;
    background: #DC143C;
    bottom: 6px;
    border-radius: 1px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 20px;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: var(--warm-gold);
    transform: translateY(-2px);
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  svg {
    color: var(--warm-gold);
    flex-shrink: 0;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  padding-top: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  margin: 0;
  opacity: 0.8;
  font-size: 0.9rem;
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 30px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
  }

  a {
    color: white;
    text-decoration: none;
    font-size: 0.9rem;
    opacity: 0.8;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 1;
    }
  }
`;

const Footer = () => {
  return (
    <FooterContainer>
      <Container>
        <FooterContent>
          <FooterSection>
            <Logo>
                        <SewaLogoContainer>
            <SewaLogoImage 
              src="/sewa-logo.png" 
              alt="Sewa USA"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'block';
              }}
            />
            <SewaLogoFallback style={{display: 'none'}}>
              SEWA<br/>USA
            </SewaLogoFallback>
            <BayAreaText>Bay Area</BayAreaText>
          </SewaLogoContainer>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <BurritoIcon></BurritoIcon>
                <LogoText>Ahaar Sewa</LogoText>
              </div>
            </Logo>
            <p>
              Part of Sewa International's Bay Area chapter, dedicated to serving our 
              community through comprehensive food security programs, including our flagship Burrito Sewa initiative.
            </p>
            <p>
              <strong>Tax ID:</strong> 20-0638718<br />
              501(c)(3) Nonprofit Organization
            </p>
            <SocialLinks>
              <SocialLink href="https://www.facebook.com/sewainternationalusa/" target="_blank" rel="noopener noreferrer">
                <FiFacebook />
              </SocialLink>
              <SocialLink href="https://twitter.com/sewausa/" target="_blank" rel="noopener noreferrer">
                <FiTwitter />
              </SocialLink>
              <SocialLink href="https://www.instagram.com/sewainternationalusa/" target="_blank" rel="noopener noreferrer">
                <FiInstagram />
              </SocialLink>
              <SocialLink href="https://www.linkedin.com/company/sewainternationalusa/" target="_blank" rel="noopener noreferrer">
                <FiLinkedin />
              </SocialLink>
            </SocialLinks>
          </FooterSection>

          <FooterSection>
            <h3>Quick Links</h3>
            <ul>
              <li><a href="#what-is-burrito-sewa">What is Ahaar Sewa</a></li>
              <li><a href="#why-sponsor">Why Sponsor</a></li>
              <li><a href="#life-events">Life Events</a></li>
              <li><a href="#past-events">Past Events</a></li>
              <li><a href="#contact">Contact Us</a></li>
              <li><a href="/event-planning">Event Planning Guides</a></li>
              <li><a href="https://www.sewausa.org/BayArea" target="_blank" rel="noopener noreferrer">Sewa Bay Area</a></li>
              <li><a href="/admin" style={{color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8rem'}}>Admin</a></li>
            </ul>
          </FooterSection>

          <FooterSection>
            <h3>Get Involved</h3>
            <ul>
              <li><a href="#contact">Sponsor an Event</a></li>
              <li><a href="#contact">Volunteer</a></li>
              <li><a href="https://www.sewausa.org/BayArea" target="_blank" rel="noopener noreferrer">Join Our Team</a></li>
              <li><a href="https://www.sewausa.org/BayArea" target="_blank" rel="noopener noreferrer">Monthly Giving</a></li>
              <li><a href="https://www.sewausa.org/BayArea" target="_blank" rel="noopener noreferrer">Corporate Partnerships</a></li>
            </ul>
          </FooterSection>

          <FooterSection>
            <h3>Contact Info</h3>
            <ContactItem>
              <FiMail />
              <div>
                              <a href="mailto:samarth@honnu.com">samarth@honnu.com</a>
              </div>
            </ContactItem>
            <ContactItem>
              <FiPhone />
              <div>
                <a href="tel:+14083007392">(408) 300-7392</a><br />
                <span>Mon-Fri, 9 AM - 6 PM</span>
              </div>
            </ContactItem>
            <ContactItem>
              <FiMapPin />
              <div>
                Sewa Community Center<br />
                691 S Milpitas Blvd<br />
                Milpitas, CA 95035
              </div>
            </ContactItem>
          </FooterSection>
        </FooterContent>

        <FooterBottom>
          <Copyright>
            © 2024 Sewa International. All rights reserved. | Ahaar Sewa - Bay Area Chapter
          </Copyright>
          <FooterLinks>
            <a href="https://www.sewausa.org/OurWork" target="_blank" rel="noopener noreferrer">Our Work</a>
            <a href="https://www.sewausa.org/OurImpact" target="_blank" rel="noopener noreferrer">Our Impact</a>
            <a href="https://www.sewausa.org/GetInvolved" target="_blank" rel="noopener noreferrer">Get Involved</a>
            <a href="https://www.sewausa.org/chapters" target="_blank" rel="noopener noreferrer">All Chapters</a>
          </FooterLinks>
        </FooterBottom>
      </Container>
    </FooterContainer>
  );
};

export default Footer; 